import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Save, Upload, Trash2, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    expandPeriodsToDates,
    groupDatesToPeriods,
    formatPeriod,
    periodKey,
} from "@/lib/holidayPeriods";

// RESOURCE-USER-LINK-001 · "Role title" is metadata describing the
// person's job on the Field Team roster. It is NOT an
// authentication role — the linked user's authentication role is
// managed in Users & Security and is presented read-only on this
// form when a linked user is selected. "Sales" is added so a Sales
// resource can carry the matching title.
const ROLES = ["Lead Installer", "Installer", "Electrician", "Surveyor", "Project Manager", "Office", "Sales"];
// FIELD-001C1A · Unified resource model — canonical selects.
const RESOURCE_KINDS = [
    { value: "internal_person",       label: "Internal person" },
    { value: "external_individual",   label: "External individual (contractor)" },
    { value: "external_organisation", label: "External organisation" },
];
const RESOURCE_TYPES = [
    // SALES-ROLE-001 · "Sales" is a first-class resource type in
    // the backend catalogue; add it to the frontend picker so a
    // Sales resource can be created from a Sales-role user.
    "Sales",
    "Electrician",
    "Panel Installer",
    "Scaffolder",
    "Groundworks Contractor",
    "General / Unspecified",
];
const PORTAL_ACCESS_OPTIONS = [
    { value: "enabled",        label: "Enabled" },
    { value: "disabled",       label: "Disabled" },
    { value: "not_applicable", label: "Not applicable" },
];
// ISO weekday labels — Monday=1 … Sunday=7 (matches backend workingDays)
const WEEKDAYS = [
    { d: 1, short: "Mo" }, { d: 2, short: "Tu" }, { d: 3, short: "We" },
    { d: 4, short: "Th" }, { d: 5, short: "Fr" }, { d: 6, short: "Sa" },
    { d: 7, short: "Su" },
];

/**
 * Add / Edit modal for a persistent engineer. Handles both create + patch
 * flows — pass an `engineer` prop to edit, or omit it to add a new one. The
 * colour swatch grid uses `api.engineerPalette()` so palette changes on the
 * backend show up here automatically.
 */
export function EngineerForm({ open, engineer, palette = [], onClose, onSaved, onOpenExisting, workloadRow, onDrillTasks, onDrillCalendar, mode = "full" }) {
    // FIELD-001C1B · Field visibility modes.
    //   * "full"          — legacy behaviour, everything visible.
    //   * "identity"      — Field Team Management: identity + classification
    //                       + portal + colour + photo. NO working days /
    //                       holidays; those live on Resource Management.
    //   * "availability"  — Resource Management edit dialog: ONLY working
    //                       days + holidays + active flag. No identity edit.
    const showIdentity = mode !== "availability";
    const showClassification = mode !== "availability";
    const showAvailability = mode !== "identity";
    const showColourAndPhoto = mode !== "availability";
    const showRoleField = mode !== "availability";
    const isEdit = !!engineer;
    // FIELD-001C1B · Availability-only mode implies edit-in-place; the
    // form works purely on an existing engineer row.
    const availabilityOnly = mode === "availability";
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [role, setRole] = useState("Installer");
    const [trade, setTrade] = useState("");
    // FIELD-001C1A · Unified resource model fields.
    const [kind, setKind] = useState("internal_person");
    const [resourceType, setResourceType] = useState("General / Unspecified");
    const [organisationName, setOrganisationName] = useState("");
    const [portalAccess, setPortalAccess] = useState("enabled");
    const [projectLeadEligible, setProjectLeadEligible] = useState(true);
    const [colour, setColour] = useState("");
    const [profilePhoto, setProfilePhoto] = useState("");
    const [photoUploading, setPhotoUploading] = useState(false);
    // DEV-024 · Half-day availability state. `workingDayPattern` is
    // authoritative for the UI; `workingDays` is derived from it on
    // save so existing backend / mobile consumers stay unchanged.
    // Values per weekday key ("1"…"7"): full | morning | afternoon | none.
    const [workingDayPattern, setWorkingDayPattern] = useState({
        "1": "full", "2": "full", "3": "full", "4": "full", "5": "full",
        "6": "none", "7": "none",
    });
    const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
    // FIELD-001C1B (clarification) · Holidays are edited as inclusive
    // Start–End periods for real absences; the backend contract stays
    // a flat ISO date array (see `@/lib/holidayPeriods`).
    const [holidayPeriods, setHolidayPeriods] = useState([]);
    const [newHolidayStart, setNewHolidayStart] = useState("");
    const [newHolidayEnd, setNewHolidayEnd] = useState("");
    const [holidayError, setHolidayError] = useState("");
    // FIELD-001C1C · Live DOM refs on the date inputs. The Add-button
    // click handler prefers ref values over React state so any browser
    // that lags one tick between native date-picker commit and the
    // React re-render still adds the period the user sees.
    const startInputRef = useRef(null);
    const endInputRef = useRef(null);
    const [active, setActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    // FIELD-IDENTITY-001B · Field Team account linking.
    //   * ``linkedUserId`` is the persistent, server-authoritative link
    //     between an internal Field Team row and an installer-role
    //     ``users`` account. Editing this is the linchpin of the safe
    //     rollout to strict identity resolution.
    //   * ``installerUsers`` is fetched on demand when the picker opens
    //     (avoids blocking modal open on a network round-trip).
    //   * Link / relink / unlink each require an explicit confirmation
    //     modal — clicking "Save" WITHOUT confirming the pending change
    //     rolls back to the persisted `engineer.linkedUserId`.
    // RESOURCE-USER-LINK-001 · "From existing user" create-mode
    // affordance. Two explicit paths at Add time:
    //   * source="existing" — search + select an eligible user;
    //     autofills name/email/linkedUserId and (only when
    //     unambiguous) a default resource type.
    //   * source="manual"   — legacy path preserved for
    //     subcontractors and organisations with no login.
    // Only surfaced for CREATE mode of an internal_person. Edit
    // flows use the existing `linkedUserSnapshot` link controls.
    const [createSource, setCreateSource] = useState("existing");
    const [userQuery, setUserQuery] = useState("");
    const [userResults, setUserResults] = useState([]);
    const [userLookupLoading, setUserLookupLoading] = useState(false);
    const [userLookupError, setUserLookupError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null); // {id,name,email,role,linked_engineer_id}
    const [linkedUserId, setLinkedUserId] = useState("");
    const [linkedUserSnapshot, setLinkedUserSnapshot] = useState(null); // {id,name,email,role}
    const [linkedUserResolution, setLinkedUserResolution] = useState(null); // "linked" | "linked_missing" | "linked_disabled" | …
    const [installerUsers, setInstallerUsers] = useState([]);
    const [installerUsersLoading, setInstallerUsersLoading] = useState(false);
    const [installerUsersError, setInstallerUsersError] = useState(null);
    const [pendingLinkChange, setPendingLinkChange] = useState(null); // {kind:"link"|"relink"|"unlink", nextUserId, nextUser}

    useEffect(() => {
        if (!open) return;
        setFirstName(engineer?.firstName || "");
        setLastName(engineer?.lastName || "");
        setEmail(engineer?.email || "");
        setMobileNumber(engineer?.mobileNumber || "");
        setRole(engineer?.role || "Installer");
        setTrade(engineer?.trade || "");
        // FIELD-IDENTITY-001B · Seed link state.
        setLinkedUserId(engineer?.linkedUserId || "");
        setLinkedUserSnapshot(engineer?.linkedUser || null);
        setLinkedUserResolution(engineer?.linkedUserResolution || null);
        setPendingLinkChange(null);
        // FIELD-001C1A · Seed the unified-resource fields with sensible
        // defaults for new rows and preserve existing values on edit.
        const seededKind = engineer?.kind || "internal_person";
        setKind(seededKind);
        setResourceType(engineer?.resourceType || "General / Unspecified");
        setOrganisationName(engineer?.organisationName || "");
        setPortalAccess(engineer?.portalAccess || (
            seededKind === "external_organisation" ? "not_applicable" : "enabled"
        ));
        setProjectLeadEligible(
            engineer?.projectLeadEligible !== undefined
                ? !!engineer.projectLeadEligible
                : seededKind === "internal_person",
        );
        setColour(engineer?.colour || (palette[0] || ""));
        setProfilePhoto(engineer?.profilePhoto || "");
        // DEV-024 · Seed the half-day pattern from the persisted row.
        // Precedence:
        //   1. explicit engineer.workingDayPattern (from a client that
        //      already saved a half-day pattern)
        //   2. derive from engineer.workingDays (legacy: existing
        //      selected days become "full", unselected days become
        //      "none" — preserves existing behaviour verbatim)
        //   3. sensible default (Mon–Fri full, weekend none) — ONLY
        //      applied to brand-new rows.
        const seededPattern = (() => {
            const raw = engineer?.workingDayPattern;
            if (raw && typeof raw === "object" && Object.keys(raw).length > 0) {
                const out = { "1":"none","2":"none","3":"none","4":"none","5":"none","6":"none","7":"none" };
                for (const [k, v] of Object.entries(raw)) {
                    const idx = parseInt(k, 10);
                    if (idx >= 1 && idx <= 7 && ["full","morning","afternoon","none"].includes(v)) {
                        out[String(idx)] = v;
                    }
                }
                return out;
            }
            if (engineer && Array.isArray(engineer.workingDays)) {
                const days = new Set(engineer.workingDays);
                const out = {};
                for (let d = 1; d <= 7; d++) out[String(d)] = days.has(d) ? "full" : "none";
                return out;
            }
            return { "1":"full","2":"full","3":"full","4":"full","5":"full","6":"none","7":"none" };
        })();
        setWorkingDayPattern(seededPattern);
        setWorkingDays(
            Object.entries(seededPattern)
                .filter(([, v]) => v !== "none")
                .map(([k]) => parseInt(k, 10))
                .sort(),
        );
        setHolidayPeriods(
            Array.isArray(engineer?.holidays) ? groupDatesToPeriods(engineer.holidays) : []
        );
        setNewHolidayStart("");
        setNewHolidayEnd("");
        setHolidayError("");
        setActive(engineer?.active ?? true);
        setErrors({});
        // RESOURCE-USER-LINK-001 · Reset the "from existing user"
        // affordance to a clean state each time the modal opens.
        // Only resets when the modal transitions to open — keeps
        // selection on validation failure (form stays open).
        setCreateSource(isEdit ? "manual" : "existing");
        setUserQuery("");
        setUserResults([]);
        setUserLookupLoading(false);
        setUserLookupError(null);
        setSelectedUser(null);
    }, [open, engineer, palette, isEdit]);

    // RESOURCE-USER-LINK-001 · Debounced user-lookup search. Fires
    // only when the "existing user" path is active + kind admits a
    // link. Runs on every ~250ms typing pause.
    useEffect(() => {
        if (!open || isEdit) return undefined;
        if (createSource !== "existing" || kind !== "internal_person") return undefined;
        let cancelled = false;
        const t = setTimeout(async () => {
            setUserLookupLoading(true);
            setUserLookupError(null);
            try {
                const resp = await api.engineerUserLookup({ q: userQuery.trim() });
                if (!cancelled) setUserResults(resp?.users || []);
            } catch (e) {
                if (!cancelled) {
                    setUserLookupError(
                        formatApiError(e?.response?.data?.detail) || e.message,
                    );
                    setUserResults([]);
                }
            } finally {
                if (!cancelled) setUserLookupLoading(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [open, isEdit, createSource, kind, userQuery]);
    // Auto-fills identity from the ``users`` document and — only
    // when the mapping is unambiguous — suggests a default
    // resource type (a Sales-role user maps to the "Sales" resource
    // type; other roles keep whatever the operator already selected).
    // Never overwrites `active`, `colour`, `photoUrl`, availability
    // or manual entries the user has just typed above.
    const applyPickedUser = (u) => {
        setSelectedUser(u);
        setLinkedUserId(u.id);
        setLinkedUserSnapshot({ id: u.id, name: u.name, email: u.email });
        // Split "First Last" once; leave manual overrides alone if
        // the operator has already typed something.
        const parts = (u.name || "").trim().split(/\s+/);
        if (parts.length >= 2) {
            if (!firstName.trim()) setFirstName(parts.slice(0, -1).join(" "));
            if (!lastName.trim())  setLastName(parts.slice(-1)[0]);
        } else if (parts[0]) {
            if (!firstName.trim()) setFirstName(parts[0]);
        }
        if (!email.trim() && u.email) setEmail(u.email);
        if ((u.role || "").toLowerCase() === "sales" && resourceType === "General / Unspecified") {
            setResourceType("Sales");
        }
        // Portal access on a picked user is always "enabled" — the
        // account already exists.
        setPortalAccess("enabled");
    };

    const clearPickedUser = () => {
        setSelectedUser(null);
        setLinkedUserId("");
        setLinkedUserSnapshot(null);
    };

    // FIELD-001C1A · Auto-adjust dependent fields when `kind` changes.
    // Organisations never log in and are never lead-eligible; external
    // individuals default to portal disabled and ineligible; internal
    // people default to portal enabled + lead-eligible.
    const onKindChange = (nextKind) => {
        setKind(nextKind);
        if (nextKind === "external_organisation") {
            setPortalAccess("not_applicable");
            setProjectLeadEligible(false);
            setEmail("");
        } else if (nextKind === "external_individual") {
            setPortalAccess("disabled");
            setProjectLeadEligible(false);
            setOrganisationName("");
        } else {
            // internal_person always defaults back to portal enabled + eligible.
            setPortalAccess("enabled");
            setProjectLeadEligible(true);
            setOrganisationName("");
        }
    };

    const emailRequired = kind === "internal_person" || (kind === "external_individual" && portalAccess === "enabled");

    const validate = () => {
        const e = {};
        // FIELD-001C1B · Person-vs-organisation validation shape.
        if (kind === "external_organisation") {
            if (!organisationName.trim()) e.organisationName = "Organisation name required";
        } else {
            if (!firstName.trim()) e.firstName = "First name required";
            if (!lastName.trim()) e.lastName = "Last name required";
            if (emailRequired) {
                if (!email.trim()) e.email = "Email is required for internal people and portal-enabled resources";
                else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) e.email = "Valid email required";
            } else if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
                e.email = "Valid email required";
            }
        }
        if (!resourceType.trim()) e.resourceType = "Resource type required";
        setErrors(e);
        return !Object.keys(e).length;
    };

    const submit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const isOrg = kind === "external_organisation";
            // FIELD-001C1B / FIELD-001C · Availability-only submissions
            // must NOT carry identity, active-status or other profile
            // fields — we're changing working days / holidays only. The
            // `active` state stays authoritative on Administration →
            // Field Team Management.
            const holidaysDates = expandPeriodsToDates(holidayPeriods);
            const body = availabilityOnly ? {
                workingDays: [...workingDays].sort(),
                workingDayPattern: { ...workingDayPattern },
                holidays: holidaysDates,
            } : {
                firstName: isOrg ? undefined : firstName.trim(),
                lastName: isOrg ? undefined : lastName.trim(),
                email: email.trim() || undefined,
                mobileNumber: mobileNumber.trim() || undefined,
                role: (kind === "internal_person") ? role : undefined,
                // FIELD-001C1B · The visible Trade selector was removed; the
                // canonical resourceType now feeds `trade` for legacy consumers.
                trade: undefined,
                kind,
                resourceType: resourceType || undefined,
                organisationName: (kind === "external_organisation" || kind === "external_individual")
                    ? (organisationName.trim() || undefined)
                    : undefined,
                portalAccess,
                projectLeadEligible: kind === "internal_person" ? projectLeadEligible : false,
                colour: isOrg ? undefined : (colour || undefined),
                profilePhoto: isOrg ? undefined : (profilePhoto.trim() || undefined),
                active,
                workingDays: isOrg ? undefined : [...workingDays].sort(),
                workingDayPattern: isOrg ? undefined : { ...workingDayPattern },
                holidays: isOrg ? undefined : holidaysDates,
                // FIELD-IDENTITY-001B · Only send linkedUserId for
                // internal_person rows AND only when a confirmed change
                // exists. External rows must not be linked (backend
                // already refuses, but we defensively omit).
                linkedUserId: (kind === "internal_person")
                    ? (linkedUserId || null)
                    : undefined,
            };
            const saved = isEdit
                ? await api.updateEngineer(engineer.id, body)
                : await api.createEngineer(body);
            toast.success(isEdit
                ? (availabilityOnly ? "Availability updated" : "Resource updated")
                : (isOrg ? "Organisation added" : "Resource added"));
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            setErrors({ form: formatApiError(err.response?.data?.detail) || err.message });
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    // DEV-024 · Half-day pattern selector. Cycles a single day through
    // full → morning → afternoon → none → full … Keeps the derived
    // `workingDays` list in sync so day-level scheduling warnings (which
    // only care about "is this day worked at all") stay authoritative.
    const DAY_STATES = ["full", "morning", "afternoon", "none"];
    const setDayPattern = (d, value) => {
        setWorkingDayPattern((prev) => {
            const next = { ...prev, [String(d)]: value };
            const derived = Object.entries(next)
                .filter(([, v]) => v !== "none")
                .map(([k]) => parseInt(k, 10))
                .sort();
            setWorkingDays(derived);
            return next;
        });
    };
    const cycleDayPattern = (d) => {
        const current = workingDayPattern[String(d)] || "none";
        const idx = DAY_STATES.indexOf(current);
        const next = DAY_STATES[(idx + 1) % DAY_STATES.length];
        setDayPattern(d, next);
    };
    const weeklyHours = Object.values(workingDayPattern).reduce((sum, v) => {
        if (v === "full") return sum + 8;
        if (v === "morning" || v === "afternoon") return sum + 4;
        return sum;
    }, 0);
    const toggleWorkingDay = (d) => setWorkingDays((prev) =>
        prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()
    );
    const isoRe = /^\d{4}-\d{2}-\d{2}$/;
    // FIELD-001C1C · The Add-button disabled state is derived from the
    // ACTUAL current Start and End values on every render — not from a
    // draft variable that could lag behind the input. `derivedEnd`
    // defaults to Start when End is blank so a valid Start alone
    // enables Add (one-day absence). A reversed range keeps Add
    // disabled AND surfaces an inline error live (before Add is
    // clicked) via `liveHolidayError`.
    const trimmedStart = (newHolidayStart || "").trim();
    const trimmedEnd = (newHolidayEnd || "").trim();
    const derivedEnd = trimmedEnd || trimmedStart;
    const startValid = isoRe.test(trimmedStart);
    const endValid = isoRe.test(derivedEnd);
    const rangeReversed = startValid && endValid && derivedEnd < trimmedStart;
    const holidayAddDisabled = !startValid || !endValid || rangeReversed;
    const liveHolidayError = rangeReversed
        ? "End date can’t be before start date."
        : holidayError;

    const addHolidayPeriod = () => {
        // FIELD-001C1C · Defensive: prefer live DOM values over React
        // state so the click always reflects what the user sees, even
        // if a browser held a native date-picker value that hadn't
        // flushed through onChange yet. Falls back to state when the
        // refs aren't populated (test environments).
        const rawStart = (startInputRef.current?.value || newHolidayStart || "").trim();
        const rawEnd = (endInputRef.current?.value || newHolidayEnd || "").trim();
        const start = rawStart;
        const end = (rawEnd || start).trim();
        if (!isoRe.test(start)) {
            setHolidayError("Enter a valid start date (end defaults to the same day for one-day absences).");
            return;
        }
        if (!isoRe.test(end)) {
            setHolidayError("Enter a valid end date, or leave it blank to reuse the start date.");
            return;
        }
        if (end < start) {
            setHolidayError("End date can’t be before start date.");
            return;
        }
        setHolidayPeriods((prev) => {
            // Avoid duplicating an identical period; otherwise append and
            // keep every previously-queued period intact (adding never
            // mutates or removes what the user already staged).
            if (prev.some((p) => p.start === start && p.end === end)) return prev;
            return [...prev, { start, end }].sort((a, b) => a.start.localeCompare(b.start));
        });
        setHolidayError("");
        setNewHolidayStart("");
        setNewHolidayEnd("");
    };
    const removeHolidayPeriod = (period) => {
        setHolidayPeriods((prev) => prev.filter((p) => !(p.start === period.start && p.end === period.end)));
    };
    // Auto-mirror Start into End when End is still blank so a single-tap
    // "one day off" flow works — but never overwrite an End the user has
    // already entered.
    const onChangeHolidayStart = (value) => {
        setNewHolidayStart(value);
        setHolidayError("");
        if (!newHolidayEnd) setNewHolidayEnd(value);
    };
    const onChangeHolidayEnd = (value) => {
        setNewHolidayEnd(value);
        setHolidayError("");
    };

    // Cache-buster on the served photo URL so re-uploads bust the browser cache.
    const photoSrc = profilePhoto
        ? (profilePhoto.startsWith("http") || profilePhoto.startsWith("data:")
            ? profilePhoto
            : `${process.env.REACT_APP_BACKEND_URL}${profilePhoto}`)
        : "";

    const onPickPhoto = async (file) => {
        if (!file || !engineer?.id) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please pick an image file");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Photo must be under 2 MB");
            return;
        }
        setPhotoUploading(true);
        try {
            const saved = await api.uploadEngineerPhoto(engineer.id, file);
            setProfilePhoto(saved.profilePhoto || "");
            toast.success("Photo uploaded");
            onSaved?.(saved);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally { setPhotoUploading(false); }
    };
    const removePhoto = async () => {
        if (!engineer?.id) return;
        setPhotoUploading(true);
        try {
            const saved = await api.deleteEngineerPhoto(engineer.id);
            setProfilePhoto("");
            toast.success("Photo removed");
            onSaved?.(saved);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
        } finally { setPhotoUploading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center px-4" data-testid="engineer-form">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
                <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-start justify-between">
                    <div>
                        <div className="text-xl text-[#131B2F] font-medium" data-testid="ef-title">
                            {availabilityOnly
                                ? `Edit availability${engineer?.displayName ? " · " + engineer.displayName : ""}`
                                : (kind === "external_organisation"
                                    ? (isEdit ? "Edit organisation" : "Add organisation")
                                    : (isEdit ? "Edit resource" : "Add resource"))}
                        </div>
                        <div className="text-xs text-[#4B5565] mt-0.5">
                            {availabilityOnly
                                ? "Adjust working days and holidays. Persists to appointment availability warnings and the calendar."
                                : (isEdit ? "Update details and re-save." : "Add someone or an organisation to the shared roster.")}
                        </div>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#F5F5F7] flex items-center justify-center text-[#4B5565]" data-testid="engineer-form-close">
                        <X size={14} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {errors.form && <div className="text-sm text-[#DA3535]">{errors.form}</div>}

                    {/* RESOURCE-USER-LINK-001 · Two explicit add
                        paths at create time. Only shown on create,
                        and only when the resource kind admits a
                        login link (internal person). External
                        individuals + organisations skip the
                        segmented control entirely — they always
                        take the manual path. */}
                    {!isEdit && !availabilityOnly && kind === "internal_person" && (
                        <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 space-y-3" data-testid="ef-source-block">
                            <div className="text-xs uppercase tracking-[0.12em] text-[#4B5565]">
                                Add source
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    data-testid="ef-source-existing"
                                    onClick={() => setCreateSource("existing")}
                                    aria-pressed={createSource === "existing"}
                                    className={`h-10 rounded-md border text-sm font-medium transition-colors ${
                                        createSource === "existing"
                                            ? "bg-[#131B2F] text-white border-[#131B2F]"
                                            : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]"
                                    }`}
                                >
                                    From existing user
                                </button>
                                <button
                                    type="button"
                                    data-testid="ef-source-manual"
                                    onClick={() => { setCreateSource("manual"); clearPickedUser(); }}
                                    aria-pressed={createSource === "manual"}
                                    className={`h-10 rounded-md border text-sm font-medium transition-colors ${
                                        createSource === "manual"
                                            ? "bg-[#131B2F] text-white border-[#131B2F]"
                                            : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]"
                                    }`}
                                >
                                    Manual (no user account)
                                </button>
                            </div>

                            {createSource === "existing" && (
                                <div className="space-y-2" data-testid="ef-user-picker">
                                    {selectedUser ? (
                                        <div
                                            className={`rounded-md border p-3 ${
                                                selectedUser.linked_engineer_id
                                                    ? "border-[#F5D680] bg-[#FEF9E7]"
                                                    : "border-[#E5E7EB] bg-[#F9FAFB]"
                                            }`}
                                            data-testid="ef-user-picker-selected"
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-[#131B2F] truncate" data-testid="ef-user-picker-selected-name">
                                                        {selectedUser.name || "(unnamed)"}
                                                    </div>
                                                    <div className="text-xs text-[#4B5565] truncate" data-testid="ef-user-picker-selected-email">
                                                        {selectedUser.email || "—"} · <span className="uppercase tracking-wide">{selectedUser.role || "user"}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    data-testid="ef-user-picker-clear"
                                                    onClick={clearPickedUser}
                                                    className="text-xs px-2 py-1 rounded-md border border-[#D1D5DB]"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                            {selectedUser.linked_engineer_id && (
                                                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="ef-user-picker-already-linked">
                                                    <span className="text-xs text-[#92400E]">
                                                        Already linked to an existing resource. Open the existing record instead of creating a duplicate.
                                                    </span>
                                                    <button
                                                        type="button"
                                                        data-testid="ef-user-picker-open-existing"
                                                        onClick={() => {
                                                            onOpenExisting?.(selectedUser.linked_engineer_id);
                                                            onClose?.();
                                                        }}
                                                        className="ml-auto text-xs px-2 py-1 rounded-md bg-[#131B2F] text-white"
                                                    >
                                                        Open existing resource
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <input
                                                data-testid="ef-user-picker-search"
                                                value={userQuery}
                                                onChange={(e) => setUserQuery(e.target.value)}
                                                placeholder="Search by name or email…"
                                                className="w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-sm outline-none focus:border-[#3E9E2E]"
                                            />
                                            {userLookupLoading && (
                                                <div className="text-xs text-[#4B5565]" data-testid="ef-user-picker-loading">Searching…</div>
                                            )}
                                            {userLookupError && (
                                                <div className="text-xs text-[#7A1E1E]" data-testid="ef-user-picker-error">{userLookupError}</div>
                                            )}
                                            {!userLookupLoading && !userLookupError && userResults.length === 0 && (
                                                <div className="text-xs text-[#4B5565]" data-testid="ef-user-picker-empty">
                                                    {userQuery
                                                        ? "No matching users. Try a different search or use Manual."
                                                        : "Start typing to find an existing user, or switch to Manual."}
                                                </div>
                                            )}
                                            {userResults.length > 0 && (
                                                <ul
                                                    role="listbox"
                                                    data-testid="ef-user-picker-results"
                                                    className="max-h-56 overflow-y-auto rounded-md border border-[#E5E7EB] divide-y divide-[#EDEFF3] bg-white"
                                                >
                                                    {userResults.map((u) => (
                                                        <li key={u.id}>
                                                            <button
                                                                type="button"
                                                                onClick={() => applyPickedUser(u)}
                                                                data-testid={`ef-user-picker-option-${u.id}`}
                                                                className="w-full text-left px-3 py-2 hover:bg-[#F5F5F7]"
                                                            >
                                                                <div className="text-sm text-[#131B2F] truncate">
                                                                    {u.name || "(unnamed)"}
                                                                    {u.linked_engineer_id && (
                                                                        <span className="ml-2 text-[10px] text-[#92400E] uppercase tracking-wide">
                                                                            already linked
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-[#4B5565] truncate">
                                                                    {u.email || "—"} · <span className="uppercase tracking-wide">{u.role || "user"}</span>
                                                                </div>
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* FIELD-001C1B · Person identity fields — hidden for external
                        organisations (organisationName IS the identity) and for
                        the availability-only editor. */}
                    {showIdentity && kind !== "external_organisation" && (
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="First name" error={errors.firstName}>
                            <input data-testid="ef-first" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                        </Field>
                        <Field label="Last name" error={errors.lastName}>
                            <input data-testid="ef-last" value={lastName} onChange={(e) => setLastName(e.target.value)}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                        </Field>
                    </div>
                    )}

    {showIdentity && kind === "internal_person" && (
        <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3 space-y-2" data-testid="ef-link-block">
            <div className="flex items-center gap-2">
                <div className="text-xs uppercase tracking-[0.12em] text-[#4B5565]">Linked login account</div>
                {linkedUserSnapshot && (
                    <span data-testid="ef-linked-badge"
                        className="text-[11px] px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#1E3A8A]">
                        Linked
                    </span>
                )}
            </div>
            <div className="text-xs text-[#4B5565]">
                Links this internal Field Team row to a staff login user. Installer, Sales and Operations accounts appear here; customer accounts are excluded. External resources cannot be linked. The linked user's authentication role is unchanged by this form.
            </div>
            {/* RESOURCE-USER-LINK-001 · Distinct linked-account
                states. The badge + edit form now render from the
                same ``linkedUser`` snapshot + ``linkedUserResolution``
                enum returned by the engineers list — no more list vs.
                edit contradiction. */}
            {linkedUserSnapshot ? (
                <div className="flex items-center justify-between gap-3 p-2 rounded border border-[#E5E7EB] bg-white"
                    data-testid="ef-linked-current">
                    <div className="min-w-0 text-sm">
                        <div className="font-medium text-[#131B2F] truncate" data-testid="ef-linked-current-name">
                            {linkedUserSnapshot.name || "(unnamed)"}
                        </div>
                        <div className="text-xs text-[#4B5565] truncate" data-testid="ef-linked-current-email">
                            {linkedUserSnapshot.email || "—"}
                            {linkedUserSnapshot.role ? <> · <span className="uppercase tracking-wide">{linkedUserSnapshot.role}</span></> : null}
                        </div>
                        <div className="text-[10px] text-[#4B5565] truncate">id: <code>{linkedUserSnapshot.id}</code></div>
                        {linkedUserResolution && linkedUserResolution !== "linked" && (
                            <div className="mt-1 text-[11px] text-[#B45309]" data-testid="ef-linked-resolution">
                                {linkedUserResolution === "linked_missing"
                                    ? "The linked user account no longer exists. Reconcile with Users & Security before re-linking."
                                    : linkedUserResolution === "linked_disabled"
                                        ? "The linked user account is disabled."
                                        : linkedUserResolution === "linked_suspended"
                                            ? "The linked user account is suspended."
                                            : linkedUserResolution === "linked_deleted"
                                                ? "The linked user account has been deleted."
                                                : "Linked account status: " + linkedUserResolution}
                            </div>
                        )}
                    </div>
                    <button type="button"
                        data-testid="ef-linked-unlink-btn"
                        onClick={() => setPendingLinkChange({ kind: "unlink", nextUserId: "", nextUser: null })}
                        className="text-xs px-2 py-1 rounded-md border border-[#F1B0B0] text-[#7A1E1E]">Unlink…</button>
                </div>
            ) : (
                <div className="text-xs text-[#4B5565]" data-testid="ef-linked-none">
                    No linked login account. This resource is standalone (no portal access).
                </div>
            )}
            <div className="flex items-center gap-2">
                <button type="button"
                    data-testid="ef-linked-choose-btn"
                    onClick={async () => {
                        setInstallerUsersError(null);
                        setInstallerUsersLoading(true);
                        try {
                            const rows = await api.listInstallerUsers();
                            setInstallerUsers(rows || []);
                        } catch (e) {
                            setInstallerUsersError(formatApiError(e?.response?.data?.detail) || e.message);
                        } finally {
                            setInstallerUsersLoading(false);
                        }
                    }}
                    className="text-xs px-2 py-1 rounded-md border border-[#D1D5DB]">
                    {linkedUserSnapshot ? "Change link…" : "Choose a staff account…"}
                </button>
                {installerUsersLoading && <span className="text-xs text-[#4B5565]" data-testid="ef-linked-loading">Loading…</span>}
                {installerUsersError && <span className="text-xs text-[#7A1E1E]" data-testid="ef-linked-load-error">{installerUsersError}</span>}
            </div>
            {installerUsers.length > 0 && (
                <select
                    data-testid="ef-linked-picker"
                    value=""
                    onChange={(e) => {
                        const uid = e.target.value;
                        if (!uid) return;
                        const u = installerUsers.find((x) => x.id === uid);
                        setPendingLinkChange({
                            kind: linkedUserSnapshot ? "relink" : "link",
                            nextUserId: uid,
                            nextUser: u ? { id: u.id, name: u.name, email: u.email } : null,
                        });
                    }}
                    className="w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-sm bg-white">
                    <option value="">Select a staff account…</option>
                    {installerUsers.map((u) => (
                        <option key={u.id} value={u.id}
                            data-testid={`ef-linked-picker-opt-${u.id}`}>
                            {(u.name || "(unnamed)")} · {u.email || "—"}
                        </option>
                    ))}
                </select>
            )}
        </div>
    )}

    {pendingLinkChange && (
        <div className="rounded-lg border border-[#F5D680] bg-[#FEF9E7] p-3 space-y-2"
            data-testid="ef-linked-confirm">
            <div className="text-sm font-semibold">Confirm {pendingLinkChange.kind}</div>
            <div className="text-xs text-[#4B5565]">
                {pendingLinkChange.kind === "unlink"
                    ? "Removing this link means the login user will no longer resolve to this Field Team row."
                    : (pendingLinkChange.kind === "relink"
                        ? `Change linked login user from "${linkedUserSnapshot?.email || linkedUserSnapshot?.id}" to "${pendingLinkChange.nextUser?.email || pendingLinkChange.nextUserId}".`
                        : `Link this row to "${pendingLinkChange.nextUser?.email || pendingLinkChange.nextUserId}". Any existing link on that account will be rejected by the server.`)}
            </div>
            <div className="flex items-center gap-2">
                <button type="button"
                    data-testid="ef-linked-confirm-yes"
                    onClick={() => {
                        setLinkedUserId(pendingLinkChange.nextUserId);
                        setLinkedUserSnapshot(pendingLinkChange.nextUser);
                        setPendingLinkChange(null);
                    }}
                    className="text-xs px-3 py-1.5 rounded-md bg-[#131B2F] text-white">Confirm</button>
                <button type="button"
                    data-testid="ef-linked-confirm-cancel"
                    onClick={() => setPendingLinkChange(null)}
                    className="text-xs px-3 py-1.5 rounded-md border border-[#D1D5DB]">Cancel</button>
            </div>
        </div>
    )}

                    {showIdentity && (
                    <Field label={
                        kind === "external_organisation"
                            ? "Business email (optional)"
                            : (emailRequired ? "Email" : "Email (optional)")
                    } error={errors.email}>
                        <input data-testid="ef-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                        {!emailRequired && (
                            <div className="mt-1 text-[11px] text-[#4B5565]" data-testid="ef-email-optional-hint">
                                {kind === "external_organisation"
                                    ? "Optional. Used for business contact only — organisations never log in."
                                    : "Not required for this resource kind and portal access setting."}
                            </div>
                        )}
                    </Field>
                    )}

                    {showClassification && (
                    <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3 space-y-3" data-testid="ef-resource-block">
                        <div className="text-xs uppercase tracking-[0.12em] text-[#4B5565]">Resource classification</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Kind">
                                <select data-testid="ef-kind" value={kind} onChange={(e) => onKindChange(e.target.value)}
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3 text-base bg-white outline-none focus:border-[#3E9E2E]">
                                    {RESOURCE_KINDS.map((k) => (
                                        <option key={k.value} value={k.value}
                                            data-testid={`ef-kind-opt-${k.value}`}>{k.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Resource type" error={errors.resourceType}>
                                <select data-testid="ef-resource-type" value={resourceType}
                                    onChange={(e) => setResourceType(e.target.value)}
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3 text-base bg-white outline-none focus:border-[#3E9E2E]">
                                    {RESOURCE_TYPES.map((t) => (
                                        <option key={t} value={t}
                                            data-testid={`ef-resource-type-opt-${t.replace(/\s+/g, '-').toLowerCase()}`}>{t}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        {kind === "external_organisation" && (
                            <Field label="Organisation name" error={errors.organisationName}>
                                <input data-testid="ef-organisation-name" value={organisationName}
                                    onChange={(e) => setOrganisationName(e.target.value)}
                                    placeholder="e.g. ScaffCo Ltd"
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                            </Field>
                        )}
                        {kind === "external_individual" && (
                            <Field label="Organisation (optional)">
                                <input data-testid="ef-organisation-name" value={organisationName}
                                    onChange={(e) => setOrganisationName(e.target.value)}
                                    placeholder="e.g. Contracting company"
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                            </Field>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Portal access">
                                <select data-testid="ef-portal-access" value={portalAccess}
                                    onChange={(e) => setPortalAccess(e.target.value)}
                                    disabled={kind === "external_organisation"}
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3 text-base bg-white outline-none focus:border-[#3E9E2E] disabled:bg-[#F5F5F7] disabled:text-[#4B5565]">
                                    {PORTAL_ACCESS_OPTIONS
                                        .filter((o) => kind !== "external_organisation" || o.value === "not_applicable")
                                        .map((o) => (
                                            <option key={o.value} value={o.value}
                                                data-testid={`ef-portal-access-opt-${o.value}`}>{o.label}</option>
                                        ))}
                                </select>
                                {kind === "external_organisation" && (
                                    <div className="mt-1 text-[11px] text-[#4B5565]">
                                        Organisations never log in — portal access is fixed to "Not applicable".
                                    </div>
                                )}
                            </Field>
                            {kind === "internal_person" && (
                                <Field label="Project Lead eligibility">
                                    <label className="inline-flex items-center gap-2 h-11" data-testid="ef-lead-eligible-row">
                                        <input type="checkbox"
                                            data-testid="ef-lead-eligible"
                                            checked={projectLeadEligible}
                                            onChange={(e) => setProjectLeadEligible(e.target.checked)}
                                            className="w-4 h-4 accent-[#3E9E2E]" />
                                        <span className="text-sm text-[#131B2F]">Eligible to be Project Lead</span>
                                    </label>
                                </Field>
                            )}
                        </div>
                    </div>
                    )}

                    {showIdentity && kind !== "external_organisation" && (
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Mobile (optional)">
                            <input data-testid="ef-mobile" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                        </Field>
                        {kind === "internal_person" && showRoleField && (
                            <Field
                                label="Role title (metadata)"
                                hint="How this person is labelled on the roster. This is NOT an access-control setting — the linked user's authentication role is managed in Users & Security."
                            >
                                <select data-testid="ef-role" value={role} onChange={(e) => setRole(e.target.value)}
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3 text-base bg-white outline-none focus:border-[#3E9E2E]">
                                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </Field>
                        )}
                    </div>
                    )}

                    {showIdentity && kind === "external_organisation" && (
                        <Field label="Business phone (optional)">
                            <input data-testid="ef-mobile" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                        </Field>
                    )}

                    {showColourAndPhoto && kind !== "external_organisation" && (
                    <Field label="Calendar colour">
                        <div className="flex flex-wrap gap-2" data-testid="ef-colours">
                            {palette.map((c) => (
                                <button key={c} type="button" onClick={() => setColour(c)}
                                    aria-label={`Colour ${c}`}
                                    data-testid={`ef-colour-${c.replace('#','')}`}
                                    className={`w-8 h-8 rounded-full transition-all ${colour === c ? "ring-2 ring-offset-2 ring-[#131B2F] scale-110" : "hover:scale-105"}`}
                                    style={{ background: c }} />
                            ))}
                        </div>
                    </Field>
                    )}

                    {showColourAndPhoto && kind !== "external_organisation" && (
                    <Field label="Profile photo">
                        {isEdit ? (
                            <div className="flex items-center gap-4" data-testid="ef-photo-block">
                                <div className="w-16 h-16 rounded-full overflow-hidden bg-[#F5F5F7] border border-[#E5E7EB] flex items-center justify-center text-lg font-medium text-[#4B5565]">
                                    {photoSrc ? (
                                        <img src={photoSrc} alt={`${firstName} ${lastName}`} className="w-full h-full object-cover" data-testid="ef-photo-preview" />
                                    ) : (
                                        (firstName?.[0] || "?").toUpperCase() + (lastName?.[0] || "").toUpperCase()
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 flex-1">
                                    <label
                                        className={`pe-btn-outline justify-center h-10 cursor-pointer ${photoUploading ? "opacity-60" : ""}`}
                                        data-testid="ef-photo-upload"
                                    >
                                        <Upload size={14} strokeWidth={1.75} /> {photoUploading ? "Uploading…" : (photoSrc ? "Replace photo" : "Upload photo")}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            data-testid="ef-photo-input"
                                            onChange={(e) => onPickPhoto(e.target.files?.[0])}
                                        />
                                    </label>
                                    {photoSrc && (
                                        <button type="button" onClick={removePhoto} data-testid="ef-photo-remove"
                                                className="text-xs text-[#DA3535] hover:text-[#B62828] transition-colors self-start px-1">
                                            <span className="inline-flex items-center gap-1"><Trash2 size={11} /> Remove photo</span>
                                        </button>
                                    )}
                                    <div className="text-[11px] text-[#4B5565]">PNG or JPG, up to 2 MB.</div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-xs text-[#4B5565]">
                                Save the resource first, then edit them to upload a headshot.
                            </div>
                        )}
                    </Field>
                    )}

                    {showAvailability && kind !== "external_organisation" && (
                    <Field label="Availability">
                        <div data-testid="ef-availability">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs text-[#4B5565]">Working days</div>
                                <div
                                    className="text-[11px] text-[#4B5565]"
                                    data-testid="ef-weekly-hours"
                                >
                                    <span className="font-semibold text-[#131B2F] tabular-nums" data-testid="ef-weekly-hours-value">
                                        {weeklyHours}
                                    </span>{" "}
                                    hours per week
                                </div>
                            </div>
                            <div className="grid grid-cols-7 gap-1.5" data-testid="ef-workdays">
                                {WEEKDAYS.map((w) => {
                                    const state = workingDayPattern[String(w.d)] || "none";
                                    const STATE_CFG = {
                                        full:      { bg: "bg-[#3E9E2E] border-[#3E9E2E] text-white",  label: "Full day",       hours: 8, dot: "text-white" },
                                        morning:   { bg: "bg-[#FEF3C7] border-[#F0B429] text-[#7A5B00]", label: "Morning only",  hours: 4, dot: "text-[#B45309]" },
                                        afternoon: { bg: "bg-[#DBEAFE] border-[#60A5FA] text-[#1E3A8A]", label: "Afternoon only", hours: 4, dot: "text-[#1E3A8A]" },
                                        none:      { bg: "bg-white border-[#E5E7EB] text-[#94A3B8]",   label: "Non-working",    hours: 0, dot: "text-[#CBD5E1]" },
                                    }[state];
                                    return (
                                        <button
                                            key={w.d} type="button"
                                            onClick={() => cycleDayPattern(w.d)}
                                            data-testid={`ef-workday-${w.d}`}
                                            data-state={state}
                                            aria-label={`${w.short} · ${STATE_CFG.label} (${STATE_CFG.hours} hours). Click to cycle.`}
                                            aria-pressed={state !== "none"}
                                            className={`h-16 rounded-md text-xs font-medium border transition-colors flex flex-col items-center justify-center gap-0.5 ${STATE_CFG.bg} hover:brightness-95`}
                                        >
                                            <span className="text-[11px] uppercase tracking-wide">{w.short}</span>
                                            <span
                                                className={`text-[10px] leading-tight ${STATE_CFG.dot}`}
                                                data-testid={`ef-workday-${w.d}-label`}
                                            >
                                                {state === "full" ? "Full" : state === "morning" ? "AM" : state === "afternoon" ? "PM" : "Off"}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-2 text-[11px] text-[#4B5565] leading-relaxed">
                                Click a day to cycle: <span className="text-[#3E9E2E] font-medium">Full</span> (8h) →{" "}
                                <span className="text-[#B45309] font-medium">AM</span> (4h) →{" "}
                                <span className="text-[#1E3A8A] font-medium">PM</span> (4h) →{" "}
                                <span className="text-[#94A3B8] font-medium">Off</span> (0h).
                            </div>

                            <div className="text-xs text-[#4B5565] mt-4 mb-2">Holidays</div>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 sm:items-end">
                                <div>
                                    <label className="block text-[11px] text-[#4B5565] mb-1" htmlFor="ef-holiday-start">Start date</label>
                                    <input
                                        id="ef-holiday-start"
                                        ref={startInputRef}
                                        type="date" value={newHolidayStart}
                                        onChange={(e) => onChangeHolidayStart(e.target.value)}
                                        onInput={(e) => onChangeHolidayStart(e.target.value)}
                                        onBlur={(e) => onChangeHolidayStart(e.target.value)}
                                        data-testid="ef-holiday-start"
                                        className="w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-sm outline-none focus:border-[#3E9E2E]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] text-[#4B5565] mb-1" htmlFor="ef-holiday-end">End date</label>
                                    <input
                                        id="ef-holiday-end"
                                        ref={endInputRef}
                                        type="date" value={newHolidayEnd}
                                        min={newHolidayStart || undefined}
                                        onChange={(e) => onChangeHolidayEnd(e.target.value)}
                                        onInput={(e) => onChangeHolidayEnd(e.target.value)}
                                        onBlur={(e) => onChangeHolidayEnd(e.target.value)}
                                        data-testid="ef-holiday-end"
                                        className="w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-sm outline-none focus:border-[#3E9E2E]"
                                    />
                                </div>
                                <button type="button" onClick={addHolidayPeriod} disabled={holidayAddDisabled}
                                        data-testid="ef-holiday-add"
                                        aria-disabled={holidayAddDisabled}
                                        className="pe-btn-outline h-10 sm:mt-0 disabled:opacity-50">
                                    <Plus size={13} strokeWidth={1.75} /> Add
                                </button>
                            </div>
                            <div className="text-[11px] text-[#4B5565] mt-1" data-testid="ef-holiday-hint">
                                End date defaults to the start date for a one-day absence.
                            </div>
                            {liveHolidayError && (
                                <div className="mt-2 text-xs text-[#DA3535]" data-testid="ef-holiday-error">
                                    {liveHolidayError}
                                </div>
                            )}
                            {holidayPeriods.length > 0 && (
                                <ul className="mt-3 space-y-1.5" data-testid="ef-holiday-list">
                                    {holidayPeriods.map((p) => {
                                        const key = periodKey(p);
                                        return (
                                            <li
                                                key={key}
                                                data-testid={`ef-holiday-period-${key}`}
                                                className="flex items-center justify-between gap-2 px-3 h-9 rounded-md bg-[#F5F5F7] border border-[#E5E7EB]"
                                            >
                                                <span className="text-sm text-[#131B2F]" data-testid={`ef-holiday-period-label-${key}`}>
                                                    {formatPeriod(p)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeHolidayPeriod(p)}
                                                    data-testid={`ef-holiday-period-remove-${key}`}
                                                    aria-label={`Remove holiday ${formatPeriod(p)}`}
                                                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs text-[#4B5565] hover:bg-[#FEECEC] hover:text-[#DA3535] transition-colors"
                                                >
                                                    <X size={12} strokeWidth={1.75} /> Remove
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </Field>
                    )}
                    {showAvailability && kind === "external_organisation" && (
                        <div className="text-xs text-[#4B5565]" data-testid="ef-org-availability-hint">
                            Availability is not individually managed for external organisations.
                        </div>
                    )}

                    {isEdit && !availabilityOnly && (
                        <label className="flex items-center gap-3 pt-2" data-testid="ef-active-row">
                            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
                                data-testid="ef-active" className="w-4 h-4 accent-[#3E9E2E]" />
                            <span className="text-sm text-[#131B2F]">Active</span>
                            <span className="text-xs text-[#4B5565]">— only active resources appear in pickers.</span>
                        </label>
                    )}

                    {isEdit && workloadRow && !availabilityOnly && (
                        <WorkloadSummarySection
                            row={workloadRow}
                            onDrillTasks={(extra) => onDrillTasks?.(workloadRow, extra)}
                            onDrillCalendar={(extra) => onDrillCalendar?.(workloadRow, extra)}
                        />
                    )}
                </div>

                <div className="px-6 pb-6 flex gap-3">
                    <button onClick={onClose} className="pe-btn-outline flex-1 justify-center h-11" data-testid="ef-cancel">Cancel</button>
                    <button
                        data-testid="ef-submit"
                        onClick={submit}
                        disabled={saving || (
                            kind === "external_organisation"
                                ? !organisationName.trim()
                                : (!firstName.trim() || !lastName.trim() || (emailRequired && !email.trim()))
                        ) || (
                            // RESOURCE-USER-LINK-001 · Block submit
                            // when the picker has flagged the chosen
                            // user as already linked — the operator
                            // must use "Open existing resource".
                            !isEdit && createSource === "existing"
                                && !!selectedUser?.linked_engineer_id
                        )}
                        className="pe-btn-primary flex-1 justify-center h-11 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? "Saving…" : (
                            <><Save size={14} strokeWidth={2} />
                                {availabilityOnly
                                    ? "Save availability"
                                    : (kind === "external_organisation"
                                        ? (isEdit ? "Save organisation" : "Add organisation")
                                        : (isEdit ? "Save resource" : "Add resource"))}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, error, children }) {
    return (
        <div>
            <label className="block text-sm text-[#4B5565] mb-1.5">{label}</label>
            {children}
            {error && <div className="mt-1 text-xs text-[#DA3535]">{error}</div>}
        </div>
    );
}

function WorkloadSummarySection({ row, onDrillTasks, onDrillCalendar }) {
    const items = [
        { label: "Open tasks",        value: row.openTaskCount,           action: () => onDrillTasks(),                       testid: "wl-open" },
        { label: "Overdue",           value: row.overdueTaskCount,        action: () => onDrillTasks({ scope: "overdue" }),   testid: "wl-overdue",  tone: row.overdueTaskCount > 0 ? "warning" : null },
        { label: "Critical",          value: row.criticalTaskCount,       action: () => onDrillTasks({ priority: "Critical" }), testid: "wl-critical", tone: row.criticalTaskCount > 0 ? "critical" : null },
        { label: "Due today",         value: row.dueTodayTaskCount,       action: () => onDrillTasks({ scope: "due_today" }), testid: "wl-due-today" },
        { label: "Completed today",   value: row.completedTodayTaskCount, action: () => onDrillTasks({ scope: "completed" }), testid: "wl-completed-today" },
        { label: "Visits today",      value: row.visitsTodayCount,        action: () => onDrillCalendar({ focus: "today" }),  testid: "wl-visits-today" },
        { label: "Visits this week",  value: row.visitsThisWeekCount,     action: () => onDrillCalendar({ focus: "week" }),   testid: "wl-visits-week" },
    ];
    return (
        <div className="mt-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3" data-testid="ef-workload">
            <div className="text-xs uppercase tracking-[0.12em] text-[#4B5565] mb-2">Workload</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {items.map((it) => {
                    const toneCls = it.tone === "warning"
                        ? "text-[#B45309]"
                        : it.tone === "critical"
                            ? "text-[#991B1B]"
                            : "text-[#131B2F]";
                    return (
                        <button
                            key={it.label}
                            onClick={it.action}
                            data-testid={`ef-${it.testid}`}
                            aria-label={`View ${it.value} ${it.label.toLowerCase()} for ${row.displayName}`}
                            className="text-left px-2.5 py-2 rounded-md bg-white border border-[#E5E7EB] hover:bg-[#F5F5F7] transition-colors"
                        >
                            <div className={`text-lg font-semibold ${toneCls}`}>{it.value}</div>
                            <div className="text-[11px] text-[#4B5565] uppercase tracking-[0.08em]">{it.label}</div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
